/**
 * "Customize Export" dialog — opened from `MedChronExportMenu`'s Export As action. Always
 * initializes from the user's own saved preferences, never from the mutable Visits
 * toolbar/`userCustomizationPreferencesStore`, so it is unaffected by unsaved toolbar edits.
 * PDF/DOCX is a Format field alongside the other options but is local to this dialog — it is
 * excluded from "Save Preferences"; a one-off Export never writes preferences.
 */
import { ExportFormat, useExportCase } from "@/hooks/useExportCase";
import { useMyExportPreferences, useSaveMyExportPreferences } from "@/hooks/useExportPreferences";
import { useMedChronExportForm } from "@/hooks/useMedChronExportForm";
import { isSessionExpiredError } from "@/services/apiService";
import { transformMedChronExportUiStateToApiPayload } from "@/services/userPreferencesService.mapper";
import { getErrorMessage } from "@/utils/errorUtils";
import { useState } from "react";
import toast from "react-hot-toast";

import { ExportCustomizationView } from "./ExportCustomizationView";

export interface ExportCustomizationProps {
  caseId: string;
  /** Used to build the downloaded file name and shown in the dialog header; not sent to the API. */
  caseName?: string | null;
  onClose: () => void;
}

export const ExportCustomization = ({ caseId, caseName, onClose }: ExportCustomizationProps) => {
  const { exportCase, isExporting } = useExportCase();
  const { uiState, isLoading, isError, errorMessage } = useMyExportPreferences();
  const { formState, isDirty, handleFieldChange, handleReset } = useMedChronExportForm(uiState);
  const { saveMyExportPreferences, isSaving } = useSaveMyExportPreferences();
  // Dialog-local, not part of `formState`: format is a per-export choice, never a saved preference.
  const [format, setFormat] = useState<ExportFormat>(ExportFormat.PDF);

  // Closes immediately: the export control this dialog was opened from carries the in-flight
  // state (shared via `medChronExportingStore`), so there is nothing left to show here.
  const handleExport = () => {
    if (!formState) return;
    const payload = transformMedChronExportUiStateToApiPayload(formState);
    exportCase(caseId, format, caseName, payload)
      .then(() => toast.success("Export ready"))
      .catch((error) => {
        // The session flow re-authenticates on its own; a toast here would be noise.
        if (isSessionExpiredError(error)) return;
        toast.error(getErrorMessage(error));
      });
    onClose();
  };

  const handleSaveAsDefault = () => {
    if (!formState) return;

    saveMyExportPreferences(formState)
      .then(() => toast.success("Preferences saved"))
      .catch((error) => toast.error(getErrorMessage(error)));
  };

  return (
    <ExportCustomizationView
      caseName={caseName}
      isLoading={isLoading}
      isError={isError}
      errorMessage={errorMessage}
      formState={formState}
      format={format}
      isDirty={isDirty}
      isSaving={isSaving}
      isExporting={isExporting(caseId, format)}
      onFieldChange={handleFieldChange}
      onFormatChange={setFormat}
      onReset={handleReset}
      onExport={handleExport}
      onSaveAsDefault={handleSaveAsDefault}
      onClose={onClose}
    />
  );
};
